import { auth } from "../auth";
import { EmptyState } from "../components/shared";
import { scoreboardBaseUrl, scoresheetV2Enabled } from "../lib/appRuntime";
import { ordinal } from "../lib/format";
import { assignLineupPlayer, availableLineupPlayers, lineupPlayerOptionLabel, lineupPlayerSectionRecord } from "../lineup";
import { memberApi } from "../memberApi";
import { offlineStore } from "../offlineDb";
import { matchLiveCopy, scoreboardLaunchUrl, scoreboardStatusLabel } from "../scoreboard";
import {
  applyScoresheetGameChange,
  chicagoDiscipline,
  createScoresheetState,
  hydrateScoresheet,
  isChicagoScoresheetGame,
  isCricketScoresheetGame,
  matchScoresheetTotals,
  reviewScoresheet,
  scoresheetGameDisplayName,
  shouldApplyScoresheetRefresh,
} from "../scoresheet";
import {
  CalendarDays,
  CheckCheck,
  ChevronRight,
  Download,
  Home,
  Pencil,
  Radio,
  RefreshCw,
  ShieldCheck,
  Target,
  Users,
  Wifi,
} from "lucide-react";
import React, { useEffect, useState } from "react";

export function MatchPrepView({
  match,
  teams,
  online,
  showCurrentMatchStatus = false,
  allowLineupManagement = false,
  playerContext = false,
  teamContext = false,
}) {
  const [matchPackage, setMatchPackage] = useState(null);
  const [status, setStatus] = useState("");
  const [entryMode, setEntryMode] = useState(null);
  const [evidenceNotice, setEvidenceNotice] = useState("");
  const [editingLineupId, setEditingLineupId] = useState(null);
  const [scoreboardGames, setScoreboardGames] = useState(null);
  const [matchApproval, setMatchApproval] = useState(null);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [liveMatch, setLiveMatch] = useState(null);
  const [liveMatchUnavailable, setLiveMatchUnavailable] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  const [organizationReady, setOrganizationReady] = useState(false);
  const [controllerWifi, setControllerWifi] = useState(null);
  const [controllerWifiBusy, setControllerWifiBusy] = useState(false);
  const [controllerWifiNotice, setControllerWifiNotice] = useState("");
  const [scheduleManagement, setScheduleManagement] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ matchDate: "", venuePubId: "", reason: "", resumeMode: "Continue" });
  useEffect(() => {
    offlineStore
      .getPreference("organizationId")
      .then((value) => setOrganizationId(value || null))
      .finally(() => setOrganizationReady(true));
  }, []);
  useEffect(() => {
    if (!match) return;
    (async () => {
      const cached = await offlineStore.getMatchPackage(match.matchId);
      if (cached) setMatchPackage(cached);
      if (online && auth.configured)
        try {
          const value = await memberApi.matchPackage(match.matchId);
          setMatchPackage(value);
          await offlineStore.saveMatchPackage(value);
        } catch (error) {
          setStatus(error.message);
        }
    })();
  }, [match?.matchId, online]);
  useEffect(() => {
    if (!match || !online || !auth.configured || entryMode) return;
    const timer = setInterval(async () => {
      try {
        const value = await memberApi.matchPackage(match.matchId);
        setMatchPackage(value);
        await offlineStore.saveMatchPackage(value);
      } catch {
        // The cached match package remains usable while connectivity recovers.
      }
    }, 10000);
    return () => clearInterval(timer);
  }, [match?.matchId, online, entryMode]);
  useEffect(() => {
    if (!match || !teamContext || !online || !auth.configured) return;
    let active = true;
    memberApi.matchScheduleChanges(match.matchId).then((value) => {
      if (!active) return;
      setScheduleManagement(value);
      setScheduleForm((current) => ({
        ...current,
        matchDate: value.matchDate,
        venuePubId: value.venuePubId || "",
        resumeMode: value.hasStarted ? "Continue" : "Continue",
      }));
    }).catch(() => {});
    return () => { active = false; };
  }, [match?.matchId, online, teamContext]);
  useEffect(() => {
    if (!showCurrentMatchStatus) {
      setLiveMatch(null);
      setLiveMatchUnavailable(false);
      return;
    }
    if (!match || !online || !auth.configured) return;
    let active = true;
    const refresh = async () => {
      try {
        const value = await memberApi.matchLiveStatus(match.matchId);
        if (active) {
          setLiveMatch(value);
          setLiveMatchUnavailable(false);
        }
      } catch {
        if (active) setLiveMatchUnavailable(true);
      }
    };
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [match?.matchId, online, showCurrentMatchStatus]);
  useEffect(() => {
    if (!match || !online || !auth.configured || !matchPackage?.canOpenScoreTools) return;
    let active = true;
    const refresh = async () => {
      try {
        const value = await memberApi.scoreboardGames(match.matchId);
        if (active) setScoreboardGames(value);
      } catch {
        // Match package status still controls entry availability if live board status is temporarily unavailable.
      }
    };
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [match?.matchId, online, matchPackage?.canOpenScoreTools]);
  useEffect(() => {
    if (!match || !online || !auth.configured || !matchPackage?.canOpenScoreTools) return;
    let active = true;
    const refresh = async () => {
      try {
        const value = await memberApi.scoreboardCompletion(match.matchId);
        if (active) setMatchApproval(value);
      } catch {
        // Board status remains available if approval status is temporarily unavailable.
      }
    };
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [match?.matchId, online, matchPackage?.canOpenScoreTools]);
  if (!match)
    return (
      <EmptyState
        icon={<Target />}
        title="No match scheduled"
        text="Your next match will appear here after the league publishes its schedule."
      />
    );
  if (!matchPackage)
    return (
      <section className="empty-state">
        <div>
          <Download />
        </div>
        <h2>{online ? "Preparing match" : "Not downloaded"}</h2>
        <p>
          {status ||
            (online
              ? "Gathering the rules, rosters, and venue for offline use."
              : "Connect before match night to download this match package.")}
        </p>
      </section>
    );
  const memberTeam = matchPackage.teams.find((team) =>
    teams.some((value) => value.registrationId === team.registrationId),
  );
  const opponent = matchPackage.teams.find(
    (team) => team.registrationId !== memberTeam?.registrationId,
  );
  const canManageLineup = allowLineupManagement && (
    memberTeam?.canManageLineup ||
    teams.find((team) => team.registrationId === memberTeam?.registrationId)
      ?.isCaptain
  );
  const lineupsConfirmed =
    matchPackage.teams.length === 2 &&
    matchPackage.teams.every((team) => team.lineup?.status === "Confirmed");
  const liveMatchDisplay = showCurrentMatchStatus
    ? liveMatch || {
        status: online
          ? liveMatchUnavailable
            ? "Unavailable"
            : "Loading"
          : "Offline",
        homeWins: null,
        awayWins: null,
        activeGameNumbers: [],
        viewerNextGameNumber: null,
        viewerNextGameName: null,
        games: [],
      }
    : null;
  const liveCopy = showCurrentMatchStatus
    ? matchLiveCopy(liveMatchDisplay, Boolean(memberTeam?.isHome))
    : null;
  if (entryMode === "scoresheet")
    return (
      <MatchScoresheetEntry
        matchPackage={matchPackage}
        online={online}
        onBack={() => setEntryMode(null)}
      />
    );
  return (
    <section className={`section-stack ${playerContext || teamContext ? "player-match-view" : ""} ${teamContext ? "team-match-view" : ""}`}>
      <div className="section-heading">
        <span className="kicker">
          ROUND {matchPackage.roundNumber} ·{" "}
          {online ? "SYNCED" : "OFFLINE COPY"}
        </span>
        <h1>
          {memberTeam?.name} <em>vs</em> {opponent?.name}
        </h1>
      </div>
      {liveCopy && (
        <article className="match-live-card" data-status={liveMatchDisplay.status} aria-live="polite">
          <header>
            <span className="kicker">{liveMatchDisplay.status === "Active" ? "LIVE MATCH" : "MATCH PROGRESS"}</span>
            <b>{liveCopy.activeText}</b>
          </header>
          <div className="match-live-records">
            <span><b>{memberTeam?.name}</b><strong>{liveMatch ? `${liveCopy.memberWins} W · ${liveCopy.opponentWins} L` : "—"}</strong></span>
            <span><b>{opponent?.name}</b><strong>{liveMatch ? `${liveCopy.opponentWins} W · ${liveCopy.memberWins} L` : "—"}</strong></span>
          </div>
          <div className="match-live-next">
            <Radio />
            <span>
              <b>{liveCopy.nextTiming}</b>
              {liveMatchDisplay.viewerNextGameNumber && (
                <small>Game {liveMatchDisplay.viewerNextGameNumber} · {liveMatchDisplay.viewerNextGameName}</small>
              )}
            </span>
          </div>
        </article>
      )}
      {teamContext && scheduleManagement && (
        <article className="match-schedule-card" data-status={scheduleManagement.operationalStatus}>
          <header>
            <CalendarDays />
            <div><span className="kicker">MATCH SCHEDULE</span><h2>{scheduleManagement.operationalStatus}</h2></div>
          </header>
          <p>{scheduleManagement.matchDate} · {scheduleManagement.venuePubName || "Pub not assigned"}</p>
          {scheduleManagement.canRespond ? (
            <button type="button" onClick={() => setScheduleOpen(true)}>Review reschedule request</button>
          ) : scheduleManagement.canPropose ? (
            <button type="button" onClick={() => setScheduleOpen(true)}>Request a new date or pub</button>
          ) : scheduleManagement.operationalStatus === "ReschedulePending" ? (
            <small>Waiting for the away captain to respond.</small>
          ) : null}
        </article>
      )}
      {matchApproval?.allGamesComplete && (
        <article className="match-approval-card" data-final={matchApproval.final}>
          <header>
            <CheckCheck />
            <div>
              <span className="kicker">COMPLETED MATCH</span>
              <h2>{matchApproval.final ? "Result approved" : "Captain approval"}</h2>
            </div>
          </header>
          <div className="match-approval-score">
            {matchApproval.teams.map((team) => (
              <span key={team.registrationId}>
                <b>{team.name}</b>
                <strong>{team.isHome ? matchApproval.homeWins : matchApproval.awayWins}</strong>
                <small>{team.approved ? "Approved" : "Awaiting approval"}</small>
              </span>
            ))}
          </div>
          <details>
            <summary>Review game winners</summary>
            {matchApproval.games.map((game) => (
              <div key={game.gameNumber}>
                <b>Game {game.gameNumber} · {game.name}</b>
                <span>{game.winnerName || "Winner unavailable"}</span>
              </div>
            ))}
          </details>
          {matchApproval.final ? (
            <p>Both captains approved the result. Player statistics are posted.</p>
          ) : !matchApproval.submittedForApproval && matchApproval.canSubmitForApproval ? (
            <button
              disabled={approvalBusy || !online}
              onClick={async () => {
                setApprovalBusy(true);
                setStatus("");
                try {
                  const value = await memberApi.submitScoreboardCompletion(match.matchId);
                  setMatchApproval(value);
                  setStatus("Your team approved the result. Waiting for the away captain.");
                } catch (error) {
                  setStatus(error.message);
                } finally {
                  setApprovalBusy(false);
                }
              }}
            >
              {approvalBusy ? "Submitting…" : "Submit and approve result"}
            </button>
          ) : matchApproval.canApproveRegistrationId ? (
            <button
              disabled={approvalBusy || !online}
              onClick={async () => {
                setApprovalBusy(true);
                setStatus("");
                try {
                  await memberApi.approveMatch(
                    match.matchId,
                    matchApproval.canApproveRegistrationId,
                  );
                  const value = await memberApi.scoreboardCompletion(match.matchId);
                  setMatchApproval(value);
                  setStatus(value.final ? "Match approved. Statistics are now posted." : "Your approval was recorded.");
                } catch (error) {
                  setStatus(error.message);
                } finally {
                  setApprovalBusy(false);
                }
              }}
            >
              {approvalBusy ? "Approving…" : "Approve completed match"}
            </button>
          ) : (
            <p>
              {matchApproval.submittedForApproval
                ? "Your team has approved. Waiting for the other captain."
                : "Waiting for the home captain to submit and approve the completed result."}
            </p>
          )}
        </article>
      )}
      <article className="readiness-card">
        <ShieldCheck />
        <div>
          <b>{lineupsConfirmed ? "Both lineups confirmed" : "Lineups first"}</b>
          <span>
            {lineupsConfirmed
              ? "Score entry is ready."
              : memberTeam?.lineup?.status === "Confirmed"
                ? "Your lineup is confirmed. Waiting for the other captain before score entry opens."
                : allowLineupManagement
                  ? "Confirm your players for tonight. Score entry opens after both teams confirm."
                  : "The captains are setting tonight's lineups. Score entry opens after both teams confirm."}
          </span>
        </div>
      </article>
      <article className="controller-wifi-card">
        <Wifi />
        <div>
          <span className="kicker">OCHE CONTROLLER</span>
          <b>Connect to the pub scoreboard network</b>
          <p>Use the rotating scorer Wi-Fi password for this match venue.</p>
          {controllerWifi ? (
            <div className="controller-wifi-secret">
              <span><small>NETWORK</small><strong>{controllerWifi.ssid}</strong></span>
              <span><small>PASSWORD</small><strong>{controllerWifi.password}</strong></span>
              <button type="button" onClick={async () => {
                await navigator.clipboard.writeText(controllerWifi.password);
                setControllerWifiNotice("Password copied.");
              }}>Copy password</button>
              <small>Version {controllerWifi.credentialVersion} · Do not share outside tonight&apos;s match.</small>
            </div>
          ) : (
            <button type="button" disabled={!online || controllerWifiBusy} onClick={async () => {
              setControllerWifiBusy(true);
              setControllerWifiNotice("");
              try {
                setControllerWifi(await memberApi.controllerWifi(match.matchId));
              } catch (error) {
                setControllerWifiNotice(error.message);
              } finally {
                setControllerWifiBusy(false);
              }
            }}>{controllerWifiBusy ? "Checking controller…" : "Show scorer Wi-Fi"}</button>
          )}
          {controllerWifiNotice && <small className="controller-wifi-notice">{controllerWifiNotice}</small>}
        </div>
      </article>
      {status && <p className="sync-notice">{status}</p>}
      {lineupsConfirmed && matchPackage.canOpenScoreTools && <section className="match-entry-options">
        <span className="kicker">MATCH NIGHT ENTRY</span>
        <p>
          Open the league scoreboard on any signed-in board. Each completed
          team turn flows into this match&apos;s live scoresheet automatically.
        </p>
        <button
          className="start-match"
          disabled={!online || !organizationReady || !organizationId}
          onClick={() => {
            const url = scoreboardLaunchUrl(scoreboardBaseUrl, match.matchId, organizationId, Date.now().toString(36));
            const opened = globalThis.open(url, "_blank");
            if (opened) opened.opener = null;
            else setStatus("Allow pop-ups to open the league scoreboard.");
          }}
        >
          <Radio />
          <span>
            <b>Open scoreboard</b>
            <small>
              {!online
                ? "Connect to open a scoring board"
                : !organizationReady
                  ? "Preparing your league…"
                  : organizationId
                    ? "Choose and score a game on this match"
                    : "Choose your organization again to open scoring"}
            </small>
          </span>
          <ChevronRight />
        </button>
        {scoreboardGames?.games?.length > 0 && (
          <div className="scoreboard-live-status" aria-live="polite">
            <span className="kicker">LIVE BOARD STATUS</span>
            {scoreboardGames.games.map((game) => (
              <div key={game.gameNumber} data-status={game.status}>
                <b>Game {game.gameNumber} · {game.name}</b>
                <span>{scoreboardStatusLabel(game)}</span>
              </div>
            ))}
          </div>
        )}
        {scoresheetV2Enabled && (
          <button
            className="start-match scoresheet-start"
            onClick={() => setEntryMode("scoresheet")}
          >
            <Pencil />
            <span>
              <b>Scoresheet entry</b>
              <small>Capture DartConnect results and corrections</small>
            </span>
            <ChevronRight />
          </button>
        )}
      </section>}
      {matchPackage.teams.map((team) => (
        <article className="detail-card" key={team.registrationId}>
          <header>
            <Users />
            <h2>{team.name}</h2>
            <strong>
              {team.lineup?.status === "Confirmed"
                ? "Confirmed"
                : `${team.isHome ? "Home" : "Away"} · waiting`}
            </strong>
          </header>
          {team.roster.map((player) => (
            <div className="detail-row" key={player.membershipId}>
              <span>
                <b>
                  {player.firstName} {player.lastName}
                </b>
                <small>{player.status}</small>
              </span>
              {player.isCaptain && <strong>Captain</strong>}
            </div>
          ))}
          {allowLineupManagement && lineupsConfirmed && matchPackage.canChangeConfirmedLineups && (
            <button
              className="lineup-change-button"
              onClick={() => {
                setStatus("");
                setEditingLineupId(team.registrationId);
              }}
            >
              Change unplayed games
            </button>
          )}
        </article>
      ))}
      {scheduleOpen && scheduleManagement && (
        <div className="lineup-picker-overlay" role="presentation" onClick={(event) => {
          if (event.currentTarget === event.target) setScheduleOpen(false);
        }}>
          <section aria-label="Match reschedule" aria-modal="true" className="lineup-player-picker match-schedule-modal" role="dialog">
            <header><div><span className="kicker">MATCH RESCHEDULE</span><h2>{scheduleManagement.canRespond ? "Approve the new schedule" : "Propose a new schedule"}</h2></div><button type="button" aria-label="Close" onClick={() => setScheduleOpen(false)}>×</button></header>
            {scheduleManagement.canRespond ? (() => {
              const pending = scheduleManagement.changes.find((change) => change.status === "Pending");
              return pending ? <div className="match-schedule-form">
                <strong>{pending.proposedMatchDate} · {pending.proposedVenuePubName}</strong>
                <p>{pending.reason}</p>
                {pending.matchHadStarted && <small>{pending.resumeMode === "Restart" ? "Scoring will restart; the current attempt remains archived." : "Scoring will continue from the saved match state."}</small>}
                <label>Response note (optional)<textarea value={scheduleForm.reason} onChange={(event) => setScheduleForm({ ...scheduleForm, reason: event.target.value })} /></label>
                <div className="match-schedule-actions"><button type="button" className="decline" disabled={scheduleBusy} onClick={async () => {
                  setScheduleBusy(true);
                  try { await memberApi.declineMatchReschedule(match.matchId, pending.id, scheduleForm.reason); setScheduleManagement(await memberApi.matchScheduleChanges(match.matchId)); setScheduleOpen(false); setStatus("The original match schedule remains in place."); }
                  catch (error) { setStatus(error.message); } finally { setScheduleBusy(false); }
                }}>Decline</button><button type="button" disabled={scheduleBusy} onClick={async () => {
                  setScheduleBusy(true);
                  try { await memberApi.approveMatchReschedule(match.matchId, pending.id); setScheduleManagement(await memberApi.matchScheduleChanges(match.matchId)); setScheduleOpen(false); setStatus("The new match schedule is approved."); }
                  catch (error) { setStatus(error.message); } finally { setScheduleBusy(false); }
                }}>{scheduleBusy ? "Saving…" : "Approve schedule"}</button></div>
              </div> : null;
            })() : <form className="match-schedule-form" onSubmit={async (event) => {
              event.preventDefault(); setScheduleBusy(true);
              try { await memberApi.proposeMatchReschedule(match.matchId, scheduleForm); setScheduleManagement(await memberApi.matchScheduleChanges(match.matchId)); setScheduleOpen(false); setStatus("The away captain was notified. The original schedule remains until approval."); }
              catch (error) { setStatus(error.message); } finally { setScheduleBusy(false); }
            }}>
              <label>New play date<input required type="date" min={new Date().toISOString().slice(0, 10)} value={scheduleForm.matchDate} onChange={(event) => setScheduleForm({ ...scheduleForm, matchDate: event.target.value })} /></label>
              <label>New pub<select required value={scheduleForm.venuePubId} onChange={(event) => setScheduleForm({ ...scheduleForm, venuePubId: event.target.value })}><option value="">Select pub</option>{scheduleManagement.pubs.map((pub) => <option key={pub.id} value={pub.id}>{pub.name}</option>)}</select></label>
              {scheduleManagement.hasStarted && <label>When play resumes<select value={scheduleForm.resumeMode} onChange={(event) => setScheduleForm({ ...scheduleForm, resumeMode: event.target.value })}><option value="Continue">Continue saved match</option><option value="Restart">Restart match (archive current attempt)</option></select></label>}
              <label>Reason<textarea required maxLength={500} value={scheduleForm.reason} onChange={(event) => setScheduleForm({ ...scheduleForm, reason: event.target.value })} /></label>
              <button disabled={scheduleBusy}>{scheduleBusy ? "Sending…" : "Send request to away captain"}</button>
            </form>}
          </section>
        </div>
      )}
      {canManageLineup && memberTeam?.lineup?.status !== "Confirmed" && (
        <CaptainLineup
          key={memberTeam.registrationId}
          matchPackage={matchPackage}
          team={memberTeam}
          onError={(message) => setStatus(message)}
          onSaved={async (value) => {
            const updated = {
              ...matchPackage,
              teams: matchPackage.teams.map((team) =>
                team.registrationId === value.teamRegistrationId
                  ? { ...team, lineup: value }
                  : team,
              ),
            };
            setMatchPackage(updated);
            await offlineStore.saveMatchPackage(updated);
            setStatus(value.message || "Your lineup is confirmed. Waiting for the other captain.");
          }}
        />
      )}
      {allowLineupManagement && editingLineupId && (
        <CaptainLineup
          key={`change-${editingLineupId}`}
          matchPackage={matchPackage}
          team={matchPackage.teams.find((team) => team.registrationId === editingLineupId)}
          amendment
          playedGameNumbers={matchPackage.playedGameNumbers || []}
          onCancel={() => setEditingLineupId(null)}
          onError={(message) => setStatus(message)}
          onSaved={async (value) => {
            const updated = {
              ...matchPackage,
              teams: matchPackage.teams.map((team) =>
                team.registrationId === value.teamRegistrationId ? { ...team, lineup: value } : team,
              ),
            };
            setMatchPackage(updated);
            await offlineStore.saveMatchPackage(updated);
            setEditingLineupId(null);
            setStatus(value.message || "Lineup change saved and match captains were notified.");
          }}
        />
      )}
      <article className="paper-card">
        <Target />
        <div>
          <b>The show must go on</b>
          <p>
            If phones, internet, or power fail, play on paper. Photograph the
            signed scorecard here when service returns.
          </p>
          <label>
            {online ? "Photograph scorecard" : "Save photo on this phone"}
            <input
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              capture="environment"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                if (online && auth.configured) {
                  await memberApi.uploadEvidence(
                    matchPackage.matchId,
                    file,
                    "Manual recovery scorecard",
                  );
                  setEvidenceNotice("Scorecard safely uploaded.");
                } else {
                  await offlineStore.saveDraft({
                    id: `evidence-${matchPackage.matchId}`,
                    matchId: matchPackage.matchId,
                    file,
                    createdAt: new Date().toISOString(),
                  });
                  setEvidenceNotice(
                    "Photo saved on this phone until reconnection.",
                  );
                }
              }}
            />
          </label>
          {evidenceNotice && <span>{evidenceNotice}</span>}
        </div>
      </article>
    </section>
  );
}

export function MatchScoresheetEntry({ matchPackage, online, onBack }) {
  const [sheet, setSheet] = useState(null);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [lastLiveUpdate, setLastLiveUpdate] = useState(null);
  const home = matchPackage.teams.find((team) => team.isHome);
  const away = matchPackage.teams.find((team) => !team.isHome);

  const remember = async (value) => {
    await offlineStore.saveScoresheetDraft(matchPackage.matchId, {
      scoresheetId: value.id,
      state: value.state,
      currentGameNumber: value.currentGameNumber,
      baseVersion: value.version || 0,
      status: value.status || "Draft",
      entrySource: value.entrySource || "Manual",
      authorityType: value.authorityType,
      canEdit: value.canEdit,
      canManageAssistants: value.canManageAssistants,
      canSubmit: value.canSubmit,
      canApprove: value.canApprove,
      resultStatus: value.resultStatus || null,
      approvedRegistrationIds: value.approvedRegistrationIds || [],
      submittedAt: value.submittedAt || null,
      assistantGrants: value.assistantGrants || [],
      assistantCandidates: value.assistantCandidates || [],
      pendingEvents: value.pendingEvents || [],
      dirty: Boolean(value.dirty),
      updatedAt: new Date().toISOString(),
    });
  };

  const draftRemote = (draft) => ({
    id: draft.scoresheetId,
    matchId: matchPackage.matchId,
    version: draft.baseVersion,
    status: draft.status || "Draft",
    entrySource: draft.entrySource || "Manual",
    authorityType: draft.authorityType,
    canEdit: draft.canEdit !== false,
    canManageAssistants: draft.canManageAssistants,
    canSubmit: draft.canSubmit,
    canApprove: draft.canApprove,
    resultStatus: draft.resultStatus || null,
    approvedRegistrationIds: draft.approvedRegistrationIds || [],
    submittedAt: draft.submittedAt || null,
    assistantGrants: draft.assistantGrants || [],
    assistantCandidates: draft.assistantCandidates || [],
    state: draft.state,
  });

  const load = async (force) => {
    setLoading(true);
    setNotice("");
    const draft = await offlineStore.getScoresheetDraft(matchPackage.matchId);
    if (online && auth.configured) {
      try {
        let remote = await memberApi
          .scoresheet(matchPackage.matchId)
          .catch((error) =>
            error.status === 404
              ? memberApi.startScoresheet(matchPackage.matchId)
              : Promise.reject(error),
          );
        let hydrated = hydrateScoresheet(remote, draft, matchPackage);
        if (draft?.dirty && !hydrated.conflict && draft.pendingEvents?.length) {
          remote = await memberApi.scoresheetEvents(remote.id, {
            expectedVersion: remote.version,
            currentGameNumber: draft.currentGameNumber,
            state: draft.state,
            events: draft.pendingEvents,
          });
          hydrated = hydrateScoresheet(remote, null, matchPackage);
          setNotice("Changes saved on this phone are now synced.");
        }
        setSheet(hydrated);
        if (
          !hydrated.canEdit &&
          (hydrated.status === "Submitted" || hydrated.status === "Finalized")
        )
          setReviewing(true);
        await remember(hydrated);
        if (hydrated.conflict)
          setNotice(
            "This scoresheet changed on another phone. Your saved draft is still on this phone for review.",
          );
      } catch (error) {
        if (draft) {
          setSheet(hydrateScoresheet(draftRemote(draft), draft, matchPackage));
          setNotice(`Using the copy saved on this phone. ${error.message}`);
        } else {
          setNotice(
            error.status === 403
              ? "Only the Home Captain, an enabled Home delegate, or an invited Away Captain can enter this scoresheet."
              : error.status === 404
                ? "Scoresheet entry is not enabled for this league yet."
                : error.message,
          );
        }
      }
    } else if (draft) {
      setSheet(hydrateScoresheet(draftRemote(draft), draft, matchPackage));
      setNotice(
        "Offline copy loaded. Changes will remain on this phone until it reconnects.",
      );
    } else if (!auth.configured) {
      const local = hydrateScoresheet(
        {
          id: `local-${matchPackage.matchId}`,
          matchId: matchPackage.matchId,
          version: 0,
          status: "Draft",
          authorityType: "HomeCaptain",
          canEdit: true,
          canManageAssistants: false,
          canSubmit: true,
          canApprove: false,
          state: createScoresheetState(matchPackage),
          assistantGrants: [],
          assistantCandidates: [],
        },
        null,
        matchPackage,
      );
      setSheet(local);
      await remember(local);
    } else {
      setNotice(
        "Connect once before match night to open and store this scoresheet on your phone.",
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    load(false);
  }, [matchPackage.matchId, online]);

  useEffect(() => {
    if (!online || !auth.configured || !sheet || saving || sheet.dirty ||
        sheet.conflict || String(sheet.id).startsWith("local-")) return;
    let cancelled = false;
    const refresh = async () => {
      if (document.activeElement?.closest?.(".scoresheet-current-game")) return;
      try {
        const remote = await memberApi.scoresheet(matchPackage.matchId);
        if (cancelled || !shouldApplyScoresheetRefresh(sheet, remote)) return;
        const refreshed = hydrateScoresheet(remote, null, matchPackage);
        refreshed.currentGameNumber = sheet.currentGameNumber;
        setSheet(refreshed);
        setLastLiveUpdate(new Date());
        await remember(refreshed);
      } catch {
        // The saved copy remains usable; the regular connection state handles messaging.
      }
    };
    const timer = window.setInterval(refresh, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [matchPackage.matchId, online, saving, sheet?.id, sheet?.version,
    sheet?.status, sheet?.dirty, sheet?.conflict]);

  const chooseGame = async (gameNumber) => {
    const next = { ...sheet, currentGameNumber: gameNumber };
    setSheet(next);
    await remember(next);
  };

  const grantAssistant = async (candidate) => {
    try {
      const grant = await memberApi.grantScoresheetAssistant(
        matchPackage.matchId,
        candidate.accountId,
      );
      const next = {
        ...sheet,
        assistantGrants: [
          ...(sheet.assistantGrants || []).filter(
            (value) => value.grantedToAccountId !== candidate.accountId,
          ),
          grant,
        ],
      };
      setSheet(next);
      await remember(next);
      setNotice(
        `${candidate.firstName} ${candidate.lastName} can now help enter this match.`,
      );
    } catch (error) {
      setNotice(error.message);
    }
  };

  const revokeAssistant = async (grant) => {
    try {
      await memberApi.revokeScoresheetAssistant(matchPackage.matchId, grant.id);
      const next = {
        ...sheet,
        assistantGrants: (sheet.assistantGrants || []).filter(
          (value) => value.id !== grant.id,
        ),
      };
      setSheet(next);
      await remember(next);
      setNotice("Away Captain assistance removed.");
    } catch (error) {
      setNotice(error.message);
    }
  };

  const commitGame = async (
    updatedGame,
    eventName,
    payload,
    correctsEventId = null,
    requestedEventId = null,
  ) => {
    const shouldSync =
      online && auth.configured && !String(sheet.id).startsWith("local-");
    if (shouldSync) setSaving(true);
    const eventId = requestedEventId || crypto.randomUUID();
    const change = applyScoresheetGameChange({
      state: sheet.state,
      updatedGame,
      eventName,
      payload,
      eventId,
      correctsEventId,
      occurredAt: new Date().toISOString(),
    });
    const nextState = change.state;
    const event = change.event;
    const pendingEvents = [...(sheet.pendingEvents || []), event];
    const local = {
      ...sheet,
      state: nextState,
      pendingEvents,
      dirty: true,
      conflict: false,
    };
    setSheet(local);
    await remember(local);
    if (!shouldSync) {
      setNotice(
        "Saved on this phone. It will sync when a connection is available.",
      );
      return;
    }

    try {
      const remote = await memberApi.scoresheetEvents(sheet.id, {
        expectedVersion: sheet.version,
        currentGameNumber: sheet.currentGameNumber,
        state: nextState,
        events: pendingEvents,
      });
      const synced = hydrateScoresheet(remote, null, matchPackage);
      setSheet(synced);
      await remember(synced);
      setNotice("Scoresheet saved.");
    } catch (error) {
      const conflicted = { ...local, conflict: error.status === 409 };
      setSheet(conflicted);
      await remember(conflicted);
      setNotice(
        error.status === 409
          ? "Another phone changed this scoresheet. Choose which copy to keep before entering more results."
          : `Saved on this phone. ${error.message}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const useServerCopy = async () => {
    await offlineStore.clearScoresheetDraft(matchPackage.matchId);
    setSheet(null);
    await load(true);
  };

  const keepPhoneCopy = async () => {
    if (!online || !sheet?.pendingEvents?.length) return;
    setSaving(true);
    try {
      const remote = await memberApi.scoresheet(matchPackage.matchId);
      const resolved = await memberApi.scoresheetEvents(remote.id, {
        expectedVersion: remote.version,
        currentGameNumber: sheet.currentGameNumber,
        state: sheet.state,
        events: sheet.pendingEvents,
      });
      const synced = hydrateScoresheet(resolved, null, matchPackage);
      setSheet(synced);
      await remember(synced);
      setNotice(
        "This phone's corrections were saved with the complete audit history.",
      );
    } catch (error) {
      setNotice(error.message);
    } finally {
      setSaving(false);
    }
  };

  const submitScoresheet = async () => {
    if (!online || sheet.dirty || sheet.conflict || !sheet.canSubmit) return;
    setSaving(true);
    try {
      const remote = await memberApi.submitScoresheet(sheet.id, sheet.version);
      const submitted = hydrateScoresheet(remote, null, matchPackage);
      setSheet(submitted);
      await remember(submitted);
      setNotice(
        "Scoresheet submitted to the league. You can still correct it if something is found later.",
      );
    } catch (error) {
      setNotice(error.message);
      if (error.status === 409) {
        const conflicted = { ...sheet, conflict: true };
        setSheet(conflicted);
        await remember(conflicted);
      }
    } finally {
      setSaving(false);
    }
  };

  const approveScoresheet = async () => {
    if (!online || !sheet.canApprove || !away?.registrationId) return;
    setSaving(true);
    try {
      await memberApi.approveMatch(matchPackage.matchId, away.registrationId);
      const remote = await memberApi.scoresheet(matchPackage.matchId);
      const approved = hydrateScoresheet(remote, null, matchPackage);
      setSheet(approved);
      await remember(approved);
      setNotice("Match result approved. The scoresheet is now final.");
    } catch (error) {
      setNotice(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="launch-screen">
        <b>OPENING MATCH SCORESHEET</b>
      </div>
    );
  if (!sheet)
    return (
      <section className="scoresheet-shell unavailable">
        <header>
          <button onClick={onBack}>‹ Match</button>
          <span>UNAVAILABLE</span>
        </header>
        <EmptyState
          icon={<Pencil />}
          title="Scoresheet unavailable"
          text={notice}
        />
      </section>
    );
  const games = sheet.state?.games || [];
  const activeIndex = Math.max(
    0,
    games.findIndex(
      (game) => Number(game.gameNumber) === Number(sheet.currentGameNumber),
    ),
  );
  const current = games[activeIndex] || games[0];
  const grant = (sheet.assistantGrants || [])[0];
  const candidate =
    (sheet.assistantCandidates || []).find(
      (value) => !grant || value.accountId === grant.grantedToAccountId,
    ) || sheet.assistantCandidates?.[0];
  const totals = matchScoresheetTotals(
    sheet.state,
    home?.registrationId,
    away?.registrationId,
  );
  const review = reviewScoresheet(
    sheet.state,
    home?.registrationId,
    away?.registrationId,
  );
  const finalized = sheet.status === "Finalized";
  return (
    <section className="scoresheet-shell">
      <header>
        <button onClick={reviewing ? () => setReviewing(false) : onBack}>
          ‹ {reviewing ? "Games" : "Match"}
        </button>
        <span>
          {saving
            ? "SAVING"
            : sheet.conflict
              ? "NEEDS REVIEW"
            : finalized
              ? "FINAL"
              : sheet.status === "Submitted"
                ? "SUBMITTED"
                : online
                  ? sheet.dirty
                    ? "SAVED LOCALLY"
                    : sheet.entrySource === "Scoreboard" || sheet.entrySource === "Mixed"
                      ? "LIVE · SYNCED"
                      : "SYNCED"
                  : "OFFLINE COPY"}
        </span>
        <button
          disabled={!online || saving}
          onClick={() => load(true)}
          aria-label="Refresh scoresheet"
        >
          <RefreshCw />
        </button>
      </header>
      <div className="scoresheet-match">
        <span className="kicker">
          LIVE SCORESHEET · {(sheet.entrySource || "Manual").toUpperCase()} ENTRY
        </span>
        <h1>
          {home?.name} <em>vs</em> {away?.name}
        </h1>
        <p>
          {sheet.authorityType === "AwayCaptainReviewer"
            ? "Reviewing as Away Captain"
            : sheet.authorityType === "AwayCaptainAssistant"
              ? "Helping the Home Captain"
              : sheet.authorityType === "HomeDelegate"
                ? "Entering as Home Team delegate"
                : "Entering as Home Captain"}
          {lastLiveUpdate && ` · Live update ${lastLiveUpdate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`}
        </p>
        <div className="scoresheet-totals">
          <span>
            <b>{totals.homeWins}</b>
            <small>Home wins</small>
            <em>
              {totals.homeGamePoints} pts · {totals.homeQuickPoints} QP
            </em>
          </span>
          <i>–</i>
          <span>
            <b>{totals.awayWins}</b>
            <small>Away wins</small>
            <em>
              {totals.awayGamePoints} pts · {totals.awayQuickPoints} QP
            </em>
          </span>
        </div>
      </div>
      {notice && (
        <p className={`sync-notice ${sheet.conflict ? "conflict" : ""}`}>
          {notice}
        </p>
      )}
      {sheet.conflict && (
        <div className="scoresheet-conflict-actions">
          <button disabled={!online || saving} onClick={useServerCopy}>
            Use server copy
          </button>
          <button disabled={!online || saving} onClick={keepPhoneCopy}>
            Keep this phone
          </button>
        </div>
      )}
      {sheet.canManageAssistants && candidate && !reviewing && (
        <article className="scoresheet-assistant">
          <div>
            <ShieldCheck />
            <span>
              <b>Away Captain assistance</b>
              <small>
                {candidate.firstName} {candidate.lastName}
              </small>
            </span>
          </div>
          {grant ? (
            <button disabled={!online} onClick={() => revokeAssistant(grant)}>
              Remove access
            </button>
          ) : (
            <button
              disabled={!online}
              onClick={() => grantAssistant(candidate)}
            >
              Allow help
            </button>
          )}
        </article>
      )}
      {reviewing ? (
        <ScoresheetReview
          sheet={sheet}
          review={review}
          totals={totals}
          home={home}
          away={away}
          online={online}
          saving={saving}
          onEdit={async (gameNumber) => {
            await chooseGame(gameNumber);
            setReviewing(false);
          }}
          onSubmit={submitScoresheet}
          onApprove={approveScoresheet}
        />
      ) : (
        <>
          <nav className="scoresheet-game-strip" aria-label="Scoresheet games">
            {games.map((game) => (
              <button
                disabled={saving || sheet.conflict}
                className={
                  game.gameNumber === current.gameNumber
                    ? "active"
                    : game.status === "Complete"
                      ? "complete"
                      : ""
                }
                onClick={() => chooseGame(game.gameNumber)}
                key={game.gameNumber}
              >
                <small>{game.sectionLabel}</small>
                <b>{game.gameNumber}</b>
                <span>{game.status === "Complete" ? "Done" : "Open"}</span>
              </button>
            ))}
          </nav>
          <ScoresheetGameEditor
            key={current.gameNumber}
            game={current}
            home={home}
            away={away}
            disabled={saving || sheet.conflict || !sheet.canEdit}
            onCommit={commitGame}
          />
          <footer className="scoresheet-progress">
            <span>
              {activeIndex + 1} of {games.length} games
            </span>
            {activeIndex < games.length - 1 ? (
              <button
                disabled={saving || sheet.conflict}
                onClick={() => chooseGame(games[activeIndex + 1]?.gameNumber)}
              >
                Next game <ChevronRight />
              </button>
            ) : (
              <button
                disabled={saving || sheet.conflict}
                onClick={() => setReviewing(true)}
              >
                Review match <CheckCheck />
              </button>
            )}
          </footer>
        </>
      )}
    </section>
  );
}

export function ScoresheetReview({
  sheet,
  review,
  totals,
  home,
  away,
  online,
  saving,
  onEdit,
  onSubmit,
  onApprove,
}) {
  const winnerName = (game) =>
    game.winnerRegistrationId === home?.registrationId
      ? home?.name
      : game.winnerRegistrationId === away?.registrationId
        ? away?.name
        : "Winner missing";
  const finalized = sheet.status === "Finalized";
  const readOnly = !sheet.canEdit;
  return (
    <section className="scoresheet-review">
      <header>
        <span className="kicker">FINAL REVIEW</span>
        <h2>
          {review.completedGames} of {review.gameCount} games complete
        </h2>
        <p>
          {sheet.canApprove
            ? "Review every game, point total, and QP before approving for the Away Team."
            : "Check every game winner, point total, and QP before the Home Captain submits."}
        </p>
      </header>
      {(sheet.status === "Submitted" || finalized) && (
        <div className="scoresheet-submitted">
          <ShieldCheck />
          <div>
            <b>
              {finalized ? "Final league result" : "Submitted to the league"}
            </b>
            <span>
              {sheet.submittedAt
                ? new Date(sheet.submittedAt).toLocaleString()
                : "Submission recorded"}
            </span>
            <small>
              {finalized
                ? "Both teams approved this result. League corrections now require an organizer."
                : sheet.canEdit
                  ? "The result and player summaries are posted pending Away Captain approval. A correction will withdraw them and reopen Draft."
                  : "The Home Captain approved this result. Review it for the Away Team."}
            </small>
          </div>
        </div>
      )}
      {review.errors.length > 0 && (
        <div className="scoresheet-review-errors" role="alert">
          <b>
            {review.errors.length} item{review.errors.length === 1 ? "" : "s"}{" "}
            need attention
          </b>
          {review.errors.map((error, index) => (
            <button
              key={`${error.gameNumber}-${error.field}-${index}`}
              onClick={() => error.gameNumber && onEdit(error.gameNumber)}
            >
              {error.gameNumber ? `Game ${error.gameNumber}: ` : ""}
              {error.message}
            </button>
          ))}
        </div>
      )}
      <div className="scoresheet-review-list">
        {sheet.state.games.map((game) => (
          <article key={game.gameNumber}>
            <div>
              <small>
                {game.sectionLabel} · Game {game.gameNumber}
              </small>
              <b>{game.label}</b>
              <span
                className={
                  game.status === "Complete" ? "complete" : "incomplete"
                }
              >
                {winnerName(game)}
              </span>
            </div>
            <dl>
              <div>
                <dt>Points</dt>
                <dd>
                  {game.homeGamePoints || 0}–{game.awayGamePoints || 0}
                </dd>
              </div>
              <div>
                <dt>QPs</dt>
                <dd>
                  {
                    (game.quickPoints || []).filter(
                      (value) => value.side === "home",
                    ).length
                  }
                  –
                  {
                    (game.quickPoints || []).filter(
                      (value) => value.side === "away",
                    ).length
                  }
                </dd>
              </div>
            </dl>
            <button onClick={() => onEdit(game.gameNumber)}>
              {readOnly ? "View" : "Edit"}
            </button>
          </article>
        ))}
      </div>
      <div className="scoresheet-review-total">
        <span>
          <small>{home?.name}</small>
          <b>{totals.homeWins}</b>
          <em>
            {totals.homeGamePoints} pts · {totals.homeQuickPoints} QP
          </em>
        </span>
        <i>–</i>
        <span>
          <small>{away?.name}</small>
          <b>{totals.awayWins}</b>
          <em>
            {totals.awayGamePoints} pts · {totals.awayQuickPoints} QP
          </em>
        </span>
      </div>
      {sheet.canApprove && (
        <div className="scoresheet-submit">
          <button disabled={!online || saving} onClick={onApprove}>
            Approve final result <ShieldCheck />
          </button>
          <p>
            {online
              ? "Approval finalizes the match and locks captain editing."
              : "Reconnect before approving this match."}
          </p>
        </div>
      )}
      {sheet.status !== "Submitted" && !finalized && (
        <div className="scoresheet-submit">
          <button
            disabled={
              !review.canSubmit ||
              !sheet.canSubmit ||
              !online ||
              sheet.dirty ||
              sheet.conflict ||
              saving
            }
            onClick={onSubmit}
          >
            Submit scoresheet <ShieldCheck />
          </button>
          {!sheet.canSubmit ? (
            <p>
              Only the Home Captain can submit. You can continue helping with
              entries and corrections.
            </p>
          ) : !online ? (
            <p>
              Reconnect before submitting. The complete draft remains safe on
              this phone.
            </p>
          ) : sheet.dirty ? (
            <p>Wait for saved changes to finish syncing before submitting.</p>
          ) : !review.canSubmit ? (
            <p>Correct the items above before submitting.</p>
          ) : (
            <p>
              Submission posts the result and player summaries for captain
              approval.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

export function ScoresheetGameEditor({ game, home, away, disabled, onCommit }) {
  const [points, setPoints] = useState({
    home: game.homeGamePoints || 0,
    away: game.awayGamePoints || 0,
  });
  const [notes, setNotes] = useState(game.notes || "");
  const [qp, setQp] = useState({
    side: "home",
    playerMembershipId: game.homePlayers?.[0]?.membershipId || "",
    entryType: "QualityPoint",
    value: "",
    quantity: "1",
    editingId: null,
  });
  const [performanceOpen, setPerformanceOpen] = useState(false);
  const homeId = home?.registrationId;
  const awayId = away?.registrationId;
  const isCricket = isCricketScoresheetGame(game);
  const isChicago = isChicagoScoresheetGame(game);
  const gameDisplayName = game.displayName || scoresheetGameDisplayName(game);
  const players =
    qp.side === "home" ? game.homePlayers || [] : game.awayPlayers || [];
  const liveCricketTotals = Object.entries(game.liveScore?.cricket || {}).reduce(
    (totals, [key, value]) => ({
      ...totals,
      [key.startsWith("home-") ? "home" : "away"]:
        totals[key.startsWith("home-") ? "home" : "away"] + (Number(value) || 0),
    }),
    { home: 0, away: 0 },
  );
  const playerLabel = (player) =>
    `${player?.firstName || ""} ${player?.lastName || ""}`.trim();
  const playerDisplayLabel = (player) => {
    const label = playerLabel(player);
    return label.length > 15 ? `${label.slice(0, 14)}…` : label;
  };
  const openPerformanceEntry = (
    side,
    playerMembershipId,
    entryType = "QualityPoint",
  ) => {
    setQp({
      side,
      playerMembershipId,
      entryType,
      value: entryType === "MarksInTurn" ? "4" : "",
      quantity: "1",
      editingId: null,
    });
    setPerformanceOpen(true);
  };
  const teamButton = (team, label, selected, onClick, buttonDisabled = disabled) => (
    <button
      disabled={buttonDisabled}
      className={selected ? "selected" : ""}
      onClick={onClick}
    >
      {label}
    </button>
  );

  const setLegDiscipline = async (leg, discipline) => {
    if (leg.selectionMode === "Fixed" || discipline === chicagoDiscipline(leg)) return;
    const eventId = crypto.randomUUID();
    const legs = game.legs.map((value) => value.legNumber === leg.legNumber
      ? {
          ...value,
          discipline: discipline || null,
          gameType: discipline === "Cricket" ? "Cricket" : discipline ? "X01" : null,
          startingScore: discipline && discipline !== "Cricket" ? Number(discipline) : null,
          status: "NotPlayed",
          winnerRegistrationId: null,
          selectionEventId: eventId,
        }
      : value);
    await onCommit(
      {
        ...game,
        legs,
        winnerRegistrationId: null,
        homeGamePoints: 0,
        awayGamePoints: 0,
        status: "InProgress",
      },
      "LegSelection",
      {
        legNumber: leg.legNumber,
        discipline: discipline || null,
        previousDiscipline: chicagoDiscipline(leg) || null,
      },
      leg.selectionEventId || null,
      eventId,
    );
  };

  const setLegWinner = async (leg, registrationId) => {
    if (isChicago && !chicagoDiscipline(leg)) return;
    const winnerRegistrationId =
      leg.winnerRegistrationId === registrationId ? null : registrationId;
    const eventId = crypto.randomUUID();
    const legs = game.legs.map((value) =>
      value.legNumber === leg.legNumber
        ? { ...value, winnerRegistrationId, status: winnerRegistrationId ? "Complete" : "NotPlayed", lastEventId: eventId }
        : value,
    );
    const homeLegWins = legs.filter(value => value.winnerRegistrationId === homeId).length;
    const awayLegWins = legs.filter(value => value.winnerRegistrationId === awayId).length;
    const gameWinnerRegistrationId = isChicago
      ? homeLegWins >= 2 ? homeId : awayLegWins >= 2 ? awayId : null
      : game.winnerRegistrationId;
    await onCommit(
      {
        ...game,
        legs,
        winnerRegistrationId: gameWinnerRegistrationId,
        homeGamePoints: gameWinnerRegistrationId === homeId ? 1 : 0,
        awayGamePoints: gameWinnerRegistrationId === awayId ? 1 : 0,
        status: gameWinnerRegistrationId ? "Complete" : "InProgress",
      },
      "LegResult",
      {
        legNumber: leg.legNumber,
        winnerRegistrationId,
        previousWinnerRegistrationId: leg.winnerRegistrationId,
      },
      leg.lastEventId || null,
      eventId,
    );
  };

  const setWinner = async (registrationId) => {
    const winnerRegistrationId =
      game.winnerRegistrationId === registrationId ? null : registrationId;
    const eventId = crypto.randomUUID();
    await onCommit(
      {
        ...game,
        winnerRegistrationId,
        resultEventId: eventId,
        homeGamePoints: winnerRegistrationId === homeId ? 1 : 0,
        awayGamePoints: winnerRegistrationId === awayId ? 1 : 0,
        status: winnerRegistrationId ? "Complete" : "InProgress",
      },
      "GameResult",
      {
        winnerRegistrationId,
        previousWinnerRegistrationId: game.winnerRegistrationId,
      },
      game.resultEventId || null,
      eventId,
    );
    setPoints({
      home: winnerRegistrationId === homeId ? 1 : 0,
      away: winnerRegistrationId === awayId ? 1 : 0,
    });
  };

  const savePoints = async () => {
    const homeGamePoints = Math.max(0, Math.min(99, Number(points.home) || 0));
    const awayGamePoints = Math.max(0, Math.min(99, Number(points.away) || 0));
    const eventId = crypto.randomUUID();
    await onCommit(
      {
        ...game,
        homeGamePoints,
        awayGamePoints,
        pointsEventId: eventId,
        status: game.status === "NotEntered" ? "InProgress" : game.status,
      },
      "GamePoints",
      {
        homeGamePoints,
        awayGamePoints,
        previousHomeGamePoints: game.homeGamePoints,
        previousAwayGamePoints: game.awayGamePoints,
      },
      game.pointsEventId || null,
      eventId,
    );
  };

  const savePerformanceEntry = async (event) => {
    event.preventDefault();
    const player = players.find(
      (value) => value.membershipId === qp.playerMembershipId,
    );
    const isMarksEntry = isCricket && qp.entryType === "MarksInTurn";
    const value = isMarksEntry
      ? Math.max(4, Math.min(6, Number(qp.value) || 4))
      : Math.max(0, Math.min(180, Number(qp.value) || 0));
    const quantity = Math.max(1, Math.min(20, Number(qp.quantity) || 1));
    if (!player || value === 0) return;
    const eventId = crypto.randomUUID();
    const existing = (game.quickPoints || []).find(
      (entry) => entry.id === qp.editingId,
    );
    const performanceEntry = {
      id: existing?.id || eventId,
      eventId,
      side: qp.side,
      playerMembershipId: player.membershipId,
      playerName: playerLabel(player),
      entryType: isCricket ? qp.entryType : "QualityPoint",
      value,
      quantity,
      metric: isCricket && qp.entryType === "MarksInTurn" ? "Marks" : "Points",
    };
    const quickPoints = existing
      ? (game.quickPoints || []).map((entry) =>
          entry.id === existing.id ? performanceEntry : entry,
        )
      : [...(game.quickPoints || []), performanceEntry];
    await onCommit(
      {
        ...game,
        quickPoints,
        status: game.status === "NotEntered" ? "InProgress" : game.status,
      },
      performanceEntry.entryType,
      performanceEntry,
      existing?.eventId || existing?.id || null,
      eventId,
    );
    setQp((current) => ({
      ...current,
      value: "",
      quantity: "1",
      editingId: null,
    }));
    setPerformanceOpen(false);
  };

  const editPerformanceEntry = (entry) => {
    setQp({
      side: entry.side,
      playerMembershipId: entry.playerMembershipId,
      entryType:
        entry.entryType || (entry.metric === "Marks" ? "MarksInTurn" : "QualityPoint"),
      value: String(entry.value),
      quantity: String(entry.quantity || 1),
      editingId: entry.id,
    });
    setPerformanceOpen(true);
  };

  const removeQuickPoint = async (quickPoint) => {
    await onCommit(
      {
        ...game,
        quickPoints: (game.quickPoints || []).filter(
          (value) => value.id !== quickPoint.id,
        ),
      },
      "QuickPointRemoved",
      {
        removedQuickPointId: quickPoint.id,
        playerMembershipId: quickPoint.playerMembershipId,
        value: quickPoint.value,
        metric: quickPoint.metric,
      },
      quickPoint.eventId || quickPoint.id,
    );
  };

  const saveNotes = async () => {
    const nextNotes = notes.trim();
    if (nextNotes === (game.notes || "")) return;
    const eventId = crypto.randomUUID();
    await onCommit(
      {
        ...game,
        notes: nextNotes,
        notesEventId: eventId,
        status: game.status === "NotEntered" ? "InProgress" : game.status,
      },
      "GameNotes",
      {
        notes: nextNotes,
        previousNotes: game.notes || "",
      },
      game.notesEventId || null,
      eventId,
    );
  };

  return (
    <article className="scoresheet-current-game">
      <header>
        <div>
          <span className="kicker">
            GAME {game.gameNumber} · {game.sectionLabel}
          </span>
          <h2>{gameDisplayName}</h2>
        </div>
        <strong>
          {game.status === "Complete"
            ? "Complete"
            : game.status === "InProgress"
              ? "Editing"
              : "Not entered"}
        </strong>
      </header>
      <div className="scoresheet-players">
        <div>
          <small>HOME PLAYERS</small>
          {game.homePlayers?.length ? (
            game.homePlayers.map((player) => (
              <span className="scoresheet-player-row home" key={player.membershipId}>
                <b title={playerLabel(player)}>{playerDisplayLabel(player)}</b>
                <button
                  disabled={disabled}
                  onClick={() =>
                    openPerformanceEntry("home", player.membershipId)
                  }
                >
                  ← QP
                </button>
              </span>
            ))
          ) : (
            <span>Lineup not set</span>
          )}
        </div>
        <div>
          <small>AWAY PLAYERS</small>
          {game.awayPlayers?.length ? (
            game.awayPlayers.map((player) => (
              <span className="scoresheet-player-row away" key={player.membershipId}>
                <button
                  disabled={disabled}
                  onClick={() =>
                    openPerformanceEntry("away", player.membershipId)
                  }
                >
                  QP →
                </button>
                <b title={playerLabel(player)}>{playerDisplayLabel(player)}</b>
              </span>
            ))
          ) : (
            <span>Lineup not set</span>
          )}
        </div>
      </div>
      {game.liveScore?.source === "Scoreboard" && (
        <section className="scoresheet-live-score">
          <span className="kicker">LIVE FROM SCOREBOARD</span>
          <div>
            <strong>
              {isCricket
                ? `${liveCricketTotals.home} marks`
                : `${game.liveScore.homeRemaining ?? "—"} remaining`}
            </strong>
            <i>–</i>
            <strong>
              {isCricket
                ? `${liveCricketTotals.away} marks`
                : `${game.liveScore.awayRemaining ?? "—"} remaining`}
            </strong>
          </div>
          <small>Updates automatically as scoreboard entries are saved.</small>
        </section>
      )}
      {game.legs?.length > 1 && (
        <section className="scoresheet-legs">
          <span className="kicker">LEG WINNERS</span>
          {game.legs.map((leg) => (
            <div key={leg.legNumber}>
              <b>{isChicago ? `${chicagoDiscipline(leg) || "Choose game"} · Leg ${leg.legNumber}` : `${gameDisplayName} · Leg ${leg.legNumber}`}</b>
              {isChicago && (
                <select
                  aria-label={`Leg ${leg.legNumber} game`}
                  disabled={disabled || leg.selectionMode === "Fixed" || Boolean(leg.winnerRegistrationId)}
                  value={chicagoDiscipline(leg)}
                  onChange={(event) => setLegDiscipline(leg, event.target.value)}
                >
                  <option value="">Choose 301, 501, or Cricket</option>
                  {(leg.allowedDisciplines || ["301", "501", "Cricket"]).map((discipline) => {
                    const usedByAnotherLeg = game.legs.some(value =>
                      value.legNumber !== leg.legNumber && chicagoDiscipline(value) === discipline && value.winnerRegistrationId);
                    return <option key={discipline} value={discipline} disabled={usedByAnotherLeg}>{discipline}</option>;
                  })}
                </select>
              )}
              {teamButton(
                home,
                "Home",
                leg.winnerRegistrationId === homeId,
                () => setLegWinner(leg, homeId),
                disabled || (isChicago && !chicagoDiscipline(leg)),
              )}
              {teamButton(
                away,
                "Away",
                leg.winnerRegistrationId === awayId,
                () => setLegWinner(leg, awayId),
                disabled || (isChicago && !chicagoDiscipline(leg)),
              )}
            </div>
          ))}
        </section>
      )}
      <section className="scoresheet-winner">
        <span className="kicker">GAME WINNER</span>
        {isChicago ? (
          <div>
            <strong>{game.winnerRegistrationId === homeId
              ? `${home?.name || "Home"} wins`
              : game.winnerRegistrationId === awayId
                ? `${away?.name || "Away"} wins`
                : "Complete the leg winners"}</strong>
            <small>The Chicago winner is calculated automatically when a side reaches two legs.</small>
          </div>
        ) : (
          <div>
            {teamButton(
              home,
              home?.name || "Home",
              game.winnerRegistrationId === homeId,
              () => setWinner(homeId),
            )}
            {teamButton(
              away,
              away?.name || "Away",
              game.winnerRegistrationId === awayId,
              () => setWinner(awayId),
            )}
          </div>
        )}
      </section>
      <section className="scoresheet-game-points">
        <span className="kicker">GAME POINTS</span>
        <div>
          <label>
            <small>Home</small>
            <input
              disabled={disabled}
              inputMode="numeric"
              value={points.home}
              onChange={(event) =>
                setPoints((value) => ({
                  ...value,
                  home: event.target.value.replace(/\D/g, "").slice(0, 2),
                }))
              }
            />
          </label>
          <i>–</i>
          <label>
            <small>Away</small>
            <input
              disabled={disabled}
              inputMode="numeric"
              value={points.away}
              onChange={(event) =>
                setPoints((value) => ({
                  ...value,
                  away: event.target.value.replace(/\D/g, "").slice(0, 2),
                }))
              }
            />
          </label>
          <button disabled={disabled} onClick={savePoints}>
            Save points
          </button>
        </div>
      </section>
      <section className="scoresheet-qp">
        <span className="kicker">QUALITY POINTS{isCricket ? " & CRICKET MARKS" : ""}</span>
        <p>
          Record qualifying player performances here. Entries can be corrected
          or removed before the scoresheet is finalized.
        </p>
        {(game.quickPoints || []).map((value) => (
          <div className="scoresheet-qp-item" key={value.id}>
            <span>
              <b>{value.playerName}</b>
              <small>
                {value.side} · {value.quantity > 1 ? `${value.quantity} × ` : ""}
                {value.entryType === "MarksInTurn" || (!value.entryType && value.metric === "Marks")
                  ? `${value.value}-Mark`
                  : `${value.value} Quality Points`}
              </small>
            </span>
            <div>
              <button disabled={disabled} onClick={() => editPerformanceEntry(value)}>
                Edit
              </button>
              <button disabled={disabled} onClick={() => removeQuickPoint(value)}>
                Remove
              </button>
            </div>
          </div>
        ))}
        <div className="scoresheet-performance-actions">
          <button
            disabled={disabled}
            onClick={() =>
              openPerformanceEntry(
                "home",
                game.homePlayers?.[0]?.membershipId || "",
              )
            }
          >
            Add Quality Points
          </button>
          {isCricket && (
            <button
              disabled={disabled}
              onClick={() =>
                openPerformanceEntry(
                  "home",
                  game.homePlayers?.[0]?.membershipId || "",
                  "MarksInTurn",
                )
              }
            >
              Add Cricket Marks
            </button>
          )}
        </div>
      </section>
      {performanceOpen && (
        <div
          className="scoresheet-performance-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPerformanceOpen(false);
          }}
        >
          <section
            aria-labelledby="performance-entry-title"
            aria-modal="true"
            className="scoresheet-performance-modal"
            role="dialog"
          >
            <header>
              <div>
                <span className="kicker">PLAYER PERFORMANCE</span>
                <h3 id="performance-entry-title">
                  {qp.editingId ? "Edit entry" : "Add entry"}
                </h3>
              </div>
              <button
                aria-label="Close performance entry"
                onClick={() => setPerformanceOpen(false)}
                type="button"
              >
                ×
              </button>
            </header>
            <form onSubmit={savePerformanceEntry}>
              {isCricket && (
                <label>
                  <span>Entry type</span>
                  <select
                    disabled={disabled}
                    value={qp.entryType}
                    onChange={(event) =>
                      setQp((value) => ({
                        ...value,
                        entryType: event.target.value,
                        value:
                          event.target.value === "MarksInTurn" ? "4" : "",
                      }))
                    }
                  >
                    <option value="QualityPoint">Quality Points</option>
                    <option value="MarksInTurn">Cricket Marks</option>
                  </select>
                </label>
              )}
              <label className="wide">
                <span>Team</span>
                <select
                  disabled={disabled}
                  value={qp.side}
                  onChange={(event) => {
                    const side = event.target.value;
                    const nextPlayers =
                      side === "home"
                        ? game.homePlayers || []
                        : game.awayPlayers || [];
                    setQp((current) => ({
                      ...current,
                      side,
                      playerMembershipId:
                        nextPlayers[0]?.membershipId || "",
                    }));
                  }}
                >
                  <option value="home">Home</option>
                  <option value="away">Away</option>
                </select>
              </label>
              <label className="wide">
                <span>Player</span>
                <select
                  disabled={disabled}
                  value={qp.playerMembershipId}
                  onChange={(event) =>
                    setQp((value) => ({
                      ...value,
                      playerMembershipId: event.target.value,
                    }))
                  }
                >
                  <option value="">Choose player</option>
                  {players.map((player) => (
                    <option
                      key={player.membershipId}
                      value={player.membershipId}
                    >
                      {playerLabel(player)}
                    </option>
                  ))}
                </select>
              </label>
              {qp.entryType === "MarksInTurn" ? (
                <label>
                  <span>Marks</span>
                  <select
                    disabled={disabled}
                    value={qp.value}
                    onChange={(event) =>
                      setQp((value) => ({
                        ...value,
                        value: event.target.value,
                      }))
                    }
                  >
                    <option value="4">4-Mark</option>
                    <option value="5">5-Mark</option>
                    <option value="6">6-Mark</option>
                  </select>
                </label>
              ) : (
                <label>
                  <span>Points</span>
                  <input
                    disabled={disabled}
                    inputMode="numeric"
                    placeholder="Points"
                    value={qp.value}
                    onChange={(event) =>
                      setQp((value) => ({
                        ...value,
                        value: event.target.value
                          .replace(/\D/g, "")
                          .slice(0, 3),
                      }))
                    }
                  />
                </label>
              )}
              <label>
                <span>Quantity</span>
                <input
                  disabled={disabled}
                  inputMode="numeric"
                  value={qp.quantity}
                  onChange={(event) =>
                    setQp((value) => ({
                      ...value,
                      quantity: event.target.value
                        .replace(/\D/g, "")
                        .slice(0, 2),
                    }))
                  }
                />
              </label>
              <p className="scoresheet-performance-summary">
                {qp.playerMembershipId
                  ? `Record ${Math.max(1, Number(qp.quantity) || 1)} × ${qp.value || "—"} ${
                      qp.entryType === "MarksInTurn"
                        ? "Mark"
                        : "Quality Points"
                    } for ${playerLabel(
                      players.find(
                        (player) =>
                          player.membershipId === qp.playerMembershipId,
                      ) || {},
                    )}`
                  : "Choose a player to record this performance."}
              </p>
              <div className="scoresheet-performance-buttons">
                <button
                  onClick={() => setPerformanceOpen(false)}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  disabled={
                    disabled || !qp.playerMembershipId || !qp.value
                  }
                  type="submit"
                >
                  {qp.editingId ? "Save change" : "Save performance"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
      <section className="scoresheet-notes">
        <label>
          <span className="kicker">GAME NOTES</span>
          <textarea
            disabled={disabled}
            maxLength="500"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Optional correction or match-night note"
          />
        </label>
        <button
          disabled={disabled || notes.trim() === (game.notes || "")}
          onClick={saveNotes}
        >
          Save note
        </button>
      </section>
    </article>
  );
}

export function CaptainLineup({ matchPackage, team, onSaved, onError, amendment = false, playedGameNumbers = [], onCancel }) {
  const existing = team.lineup?.assignments?.games || [];
  const formatGames = (matchPackage.matchFormat?.sections || []).flatMap(
    (section, sectionIndex) =>
      (section.games || []).map((game) => ({
        ...game,
        sectionKey: String(section.id || `section-${sectionIndex}`),
        sectionName:
          section.name || section.label || `Section ${sectionIndex + 1}`,
      })),
  );
  const games = existing.length
    ? existing
    : formatGames.map((game, index) => ({
        gameNumber: game.gameNumber || index + 1,
        label: game.label || game.name || game.gameType || `Game ${index + 1}`,
        playerIds: [],
      }));
  const [assignments, setAssignments] = useState({ games });
  const [saving, setSaving] = useState(false);
  const [playerPicker, setPlayerPicker] = useState(null);
  const definitionFor = (game, index) =>
    formatGames.find(
      (value) => Number(value.gameNumber) === Number(game.gameNumber),
    ) || formatGames[index];
  const sectionFor = (game, index) =>
    definitionFor(game, index)?.sectionKey || `game-${index}`;
  const assignPlayer = (gameIndex, slotIndex, playerId) =>
    setAssignments((value) => ({
      games: assignLineupPlayer(
        value.games,
        gameIndex,
        slotIndex,
        playerId,
        value.games.map(sectionFor),
      ),
    }));
  const pickerGame = playerPicker ? assignments.games[playerPicker.gameIndex] : null;
  const pickerDefinition = playerPicker
    ? definitionFor(pickerGame, playerPicker.gameIndex)
    : null;
  const pickerSection = playerPicker
    ? sectionFor(pickerGame, playerPicker.gameIndex)
    : null;
  const pickerSelectedId = playerPicker
    ? pickerGame?.playerIds?.[playerPicker.slotIndex] || ""
    : "";
  const pickerPlayers = playerPicker
    ? availableLineupPlayers(
        team.roster,
        assignments.games,
        playerPicker.gameIndex,
        playerPicker.slotIndex,
        assignments.games.map(sectionFor),
      )
    : [];
  const save = async (confirm) => {
    setSaving(true);
    try {
      const value = auth.configured
        ? await memberApi.saveLineup(
            matchPackage.matchId,
            team.registrationId,
            assignments,
            confirm,
          )
        : {
            id: "demo",
            teamRegistrationId: team.registrationId,
            status: confirm ? "Confirmed" : "Draft",
            assignments,
            updatedAt: new Date().toISOString(),
          };
      await onSaved(value);
    } catch (error) {
      onError?.(error.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <article className="lineup-card">
      <span className="kicker">CAPTAIN TOOLS</span>
      <h2>{amendment ? `Change ${team.name} lineup` : "Set your lineup"}</h2>
      <p>
        {amendment ? "Only games that have not started can be changed. Both captains and active delegates will be notified." : "Select players for each game. A player can appear once in each section and becomes available again in the next section."}
      </p>
      {games.length ? (
        assignments.games.map((game, index) => {
          const definition = definitionFor(game, index);
          const section = sectionFor(game, index);
          const firstInSection =
            index === 0 ||
            sectionFor(assignments.games[index - 1], index - 1) !== section;
          const requiredPlayers = Math.max(1, Number(definition?.playersPerTeam) || 1);
          const played = playedGameNumbers.includes(Number(game.gameNumber || index + 1));
          return (
            <React.Fragment key={game.gameNumber || index}>
              {firstInSection && definition?.sectionName && (
                <span className="lineup-section-label">
                  {definition.sectionName}
                </span>
              )}
              <div className="lineup-game">
                <b>
                  {game.gameNumber || index + 1}.{" "}
                  {game.label || `Game ${index + 1}`}
                </b>
                {played && <small>Already started · locked</small>}
                <div className="lineup-player-selects">
                  {Array.from({ length: requiredPlayers }, (_, slotIndex) => {
                    const selectedId = game.playerIds?.[slotIndex] || "";
                    return (
                      <label key={slotIndex}>
                        {requiredPlayers > 1 && <span>Player {slotIndex + 1}</span>}
                        <button
                          className={`lineup-player-trigger${selectedId ? " assigned" : ""}`}
                          aria-label={`${game.label || `Game ${index + 1}`} player ${slotIndex + 1}`}
                          disabled={played}
                          type="button"
                          title={selectedId
                            ? lineupPlayerOptionLabel(
                                team.roster.find((player) => player.membershipId === selectedId),
                                section,
                              )
                            : "Select player"}
                          onClick={() => setPlayerPicker({ gameIndex: index, slotIndex })}
                        >
                          {selectedId
                            ? lineupPlayerOptionLabel(
                                team.roster.find((player) => player.membershipId === selectedId),
                                section,
                              )
                            : "Select player"}
                          <ChevronRight size={18} />
                        </button>
                      </label>
                    );
                  })}
                </div>
              </div>
            </React.Fragment>
          );
        })
      ) : (
        <p className="muted-copy">
          This match format has no game assignments yet. The roster is still
          available offline.
        </p>
      )}
      <div className="lineup-actions">
        {amendment ? <button disabled={saving} onClick={onCancel}>Cancel</button> : <button disabled={saving} onClick={() => save(false)}>Save draft</button>}
        <button disabled={saving} onClick={() => save(true)}>
          {saving ? "Saving…" : amendment ? "Save lineup changes" : "Confirm lineup"}
        </button>
      </div>
      {playerPicker && (
        <div
          className="lineup-picker-overlay"
          role="presentation"
          onClick={(event) => {
            if (event.currentTarget === event.target) setPlayerPicker(null);
          }}
        >
          <section
            aria-label="Select lineup player"
            aria-modal="true"
            className="lineup-player-picker"
            role="dialog"
          >
            <header>
              <div>
                <span className="kicker">SET YOUR LINEUP</span>
                <h2>
                  {pickerGame?.gameNumber || playerPicker.gameIndex + 1}.{" "}
                  {pickerGame?.label || `Game ${playerPicker.gameIndex + 1}`}
                </h2>
                <p>{pickerDefinition?.sectionName || "Match section"} record</p>
              </div>
              <button type="button" aria-label="Close player selection" onClick={() => setPlayerPicker(null)}>×</button>
            </header>
            <div className="lineup-picker-list">
              {!pickerPlayers.length && <p>No eligible players are available in this section.</p>}
              {pickerPlayers.map((player) => {
                const record = lineupPlayerSectionRecord(player, pickerSection);
                return (
                  <button
                    className={player.membershipId === pickerSelectedId ? "selected" : ""}
                    key={player.membershipId}
                    type="button"
                    onClick={() => {
                      assignPlayer(playerPicker.gameIndex, playerPicker.slotIndex, player.membershipId);
                      setPlayerPicker(null);
                    }}
                  >
                    <span>{player.firstName} {player.lastName}</span>
                    <strong>{record.wins}–{record.losses}</strong>
                    <small>{record.percentageLabel}</small>
                  </button>
                );
              })}
            </div>
            {pickerSelectedId && (
              <button
                className="lineup-remove-assignment"
                type="button"
                onClick={() => {
                  assignPlayer(playerPicker.gameIndex, playerPicker.slotIndex, "");
                  setPlayerPicker(null);
                }}
              >
                Remove assignment
              </button>
            )}
            <button className="lineup-picker-cancel" type="button" onClick={() => setPlayerPicker(null)}>
              Cancel
            </button>
          </section>
        </div>
      )}
    </article>
  );
}

export function SeasonLeaderboardModal({ value, onClose }) {
  if (!value) return null;
  return (
    <div
      className="lineup-picker-overlay"
      role="presentation"
      onClick={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <section
        aria-label={`${value.title} standings`}
        aria-modal="true"
        className="lineup-player-picker season-leaderboard-modal"
        role="dialog"
      >
        <header>
          <div>
            <span className="kicker">PLAYER PLACEMENT</span>
            <h2>{value.title}</h2>
            <p>{value.context}</p>
          </div>
          <button type="button" aria-label="Close player standings" onClick={onClose}>×</button>
        </header>
        <div className="season-leaderboard-list">
          {!value.entries.length && <p>No ranked players yet.</p>}
          {value.entries.map((entry, index) => (
            <div
              className={`${entry.isCurrentPlayer ? "current" : ""}${entry.isCurrentPlayer && index >= 10 ? " appended" : ""}`}
              key={entry.playerMembershipId}
            >
              <strong>{entry.rank ? ordinal(entry.rank) : "NR"}</strong>
              <span>
                <b>{entry.playerName}{entry.isCurrentPlayer && <em>YOU</em>}</b>
                <small>{entry.teamName}</small>
              </span>
              <output>{entry.total}</output>
            </div>
          ))}
        </div>
        <button className="lineup-picker-cancel" type="button" onClick={onClose}>Close</button>
      </section>
    </div>
  );
}
